import React, { useState, useEffect, useContext, MouseEvent } from "react";
import styled, { css } from "styled-components";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Modal from "bootstrap/js/dist/modal";
import { all_routes } from "../../router/all_routes";
import { Link, NavLink, useNavigate } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import { CreditCard, Calendar } from "lucide-react";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import { Info } from "react-feather";
import { formatCustomDateTime } from "../../../utils/dateUtils";
const MySwal = withReactContent(Swal);
const showAlert = (config: any) => MySwal.fire(config);
const route = all_routes;

const SubscriptionLoader = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Loading...</span>
    </div>
    <span className="ms-2 text-muted">Loading subscription data...</span>
  </div>
);

type Accent = string;
const TEMPLATE_ACCENTS: Accent[] = [
  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "linear-gradient(135deg, #f6d365 0%, #fda085 100%)",
  "linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)",
  "linear-gradient(135deg, #c3cfe2 0%, #c3cfe2 100%)",
];

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding: 1rem;
  @media (min-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled.div<{ selected: boolean; accent: Accent }>`
  position: relative;
  background: ${({ accent }) => accent};
  border-radius: 1rem;
  padding: 4rem 1.5rem 1.5rem;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.3s;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 200px;
  ${({ selected }) =>
    selected
      ? css`
          box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.8);
          transform: scale(1.03);
        `
      : css`
          &:hover {
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
          }
        `}
`;

const DateInputWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 0.5rem;
  background: #fff;
  margin-left: auto;
  width: auto;
  padding: 0.25rem 0.5rem;
  width: fit-content;
  gap: 0.25rem;
  flex: 0 0 auto;
`;
const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid #ccc;
  border-radius: 0.5rem;
  background: #fafafa;
  align-items: center;
  & > :not(${DateInputWrapper}) {
    flex: 1;
  }

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: #444;
  min-width: 140px;
`;

const ModalContent = styled.div`
  border-radius: 1rem;
  padding: 1.5rem;
  transition: transform 0.3s ease, opacity 0.3s ease;
  &:enter {
    opacity: 0;
    transform: translateY(-10px);
  }
  &:enter-active {
    opacity: 1;
    transform: translateY(0);
  }
  &:leave {
    opacity: 1;
    transform: translateY(0);
  }
  &:leave-active {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

const TableContainer = styled.div`
  max-height: calc(5 * 70px);
  min-height: calc(5 * 70px);
  overflow-y: auto;
`;

const EditSectionCard = styled.div`
  background: #f8f9fb;
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  padding: 1.25rem;
  height: 100%;
`;

const EditSectionTitle = styled.p`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const ToggleBtn = styled.button<{ active: boolean; variant: "success" | "danger" | "primary" }>`
  flex: 1;
  border-radius: 0.5rem;
  font-size: 13px;
  font-weight: 500;
  padding: 0.45rem 0.75rem;
  border: 1.5px solid ${({ active, variant }) =>
    active
      ? variant === "success" ? "#22c55e" : variant === "danger" ? "#ef4444" : "#4f46e5"
      : "#e2e8f0"};
  background: ${({ active, variant }) =>
    active
      ? variant === "success" ? "#f0fdf4" : variant === "danger" ? "#fef2f2" : "#eef2ff"
      : "#fff"};
  color: ${({ active, variant }) =>
    active
      ? variant === "success" ? "#16a34a" : variant === "danger" ? "#dc2626" : "#4338ca"
      : "#94a3b8"};
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  &:hover {
    border-color: ${({ variant }) =>
    variant === "success" ? "#22c55e" : variant === "danger" ? "#ef4444" : "#4f46e5"};
  }
`;

interface SubscriptionOption {
  subscription_id: number;
  subscription_display_name: string;
  max_users: number;
  currency_code: string;
  amount: string;
  interval_length: number;
  interval_display_name: string;
}
interface Subscription {
  tenant_subscription_id: string;
  display_name: string;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  is_active: string | boolean | null;
  is_expired: string | boolean | null;
  subscription_id: number;
  number_of_user: number | null;
  storage?: {
    total_tenant_storage: string;
    total_tenant_used_storage: string;
  };
}

const SubscriptionsManagement: React.FC = () => {
  const { auth, setAuth, selectedTenantId } = useContext(AuthContext);
  const userRole = useTenantRole(selectedTenantId || 0);
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionOptions, setSubscriptionOptions] = useState<
    SubscriptionOption[]
  >([]);
  const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState<
    number[]
  >([]);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editSubscriptionId, setEditSubscriptionId] = useState<string | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [editStartDate, setEditStartDate] = useState<Date | null>(null);
  const [editEndDate, setEditEndDate] = useState<Date | null>(null);
  const [editIsActive, setEditIsActive] = useState<boolean>(false);
  const [editIsExpired, setEditIsExpired] = useState<boolean>(false);
  const [editSubscriptionOptionId, setEditSubscriptionOptionId] = useState<
    number | null
  >(null);
  const [numberOfUsers, setNumberOfUsers] = useState<number>(0);
  const [editNumberOfUsers, setEditNumberOfUsers] = useState<number>(0);
  const [origNumberOfUsers, setOrigNumberOfUsers] = useState<number>(0);
  const rowsPerPage = 5;
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const formatBytes = (bytes: number): string => {
    if (isNaN(bytes) || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const validIds = subscriptions
        .filter((item) => item.tenant_subscription_id)
        .map((item) => item.tenant_subscription_id);
      setSelectedItems(validIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (subscriptionId: string) => {
    setSelectedItems((prev) => {
      const newSelected = prev.includes(subscriptionId)
        ? prev.filter((id) => id !== subscriptionId)
        : [...prev, subscriptionId];

      const validCurrentData = subscriptions.filter(
        (item) => item.tenant_subscription_id
      );
      setSelectAll(
        newSelected.length === validCurrentData.length &&
        validCurrentData.length > 0
      );
      return newSelected;
    });
  };

  useEffect(() => {
    if (!selectedTenantId) return;
    (async () => {
      const authHeader = localStorage.getItem("authCred") || "";
      const res = await fetch(`${API_BASE_URL}/subscription/`, {
        headers: {
          Authorization: `Basic ${authHeader}`,
          "Content-Type": "application/json",
        },
      });
      if (res.ok) {
        const { results } = await res.json();

        const activePlans = (results || []).filter(
          (opt: any) =>
            (opt.is_active === true || opt.is_active === "True") &&
            (opt.subscription_display_name || "")
              .toLowerCase()
              .includes("cloudee sales")
        );
        setSubscriptionOptions(activePlans);
      }
    })();
  }, [selectedTenantId]);

  useEffect(() => {
    fetchSubscriptions(currentPage);
  }, [selectedTenantId, currentPage]);

  useEffect(() => {
    if (subscriptions.length > 0) {
      const validCurrentData = subscriptions.filter(
        (item) => item.tenant_subscription_id
      );
      const allCurrentSelected = validCurrentData.every(
        (item) =>
          item.tenant_subscription_id &&
          selectedItems.includes(item.tenant_subscription_id)
      );
      setSelectAll(allCurrentSelected && selectedItems.length > 0);
    } else {
      setSelectAll(false);
    }
  }, [subscriptions, selectedItems]);

  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [currentPage]);

  useEffect(() => {
    ensureModalCleanup();
  }, []);

  const fetchSubscriptions = async (page: number) => {
    setIsLoading(true);
    try {
      setSubscriptions([]);
      const authHeader = localStorage.getItem("authCred") || "";
      const res = await fetch(
        `${API_BASE_URL}/tenant-subscription/?tenant_id=${selectedTenantId}&page=${page}&page_size=${rowsPerPage}`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch subscriptions");
      const data = await res.json();
      setSubscriptions(
        data.results.map((item: any) => ({
          tenant_subscription_id: item.tenant_subscription_id,
          display_name: item.subscription_display_name,
          subscription_start_date: item.subscription_start_date,
          subscription_end_date: item.subscription_end_date,
          is_active: item.is_active,
          is_expired: item.is_expired,
          subscription_id: item.subscription_id,
          number_of_user: item.number_of_user ?? null,
          storage: item.storage,
        }))
      );
      setTotalCount(data.count);
      setTotalPages(Math.ceil(data.count / rowsPerPage));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const selectSubscription = (id: number) => {
    setSelectedSubscriptionIds([id]);
  };

  const ensureModalCleanup = () => {
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";

    document.body.classList.remove("modal-open");
  };

  const openAddSubscriptionModal = () => {
    ensureModalCleanup();

    setSelectedSubscriptionIds(
      subscriptionOptions.length > 0
        ? [subscriptionOptions[0].subscription_id]
        : []
    );
    setStartDate(new Date());
    setNumberOfUsers(0);

    const modalEl = document.getElementById("add_subscription");
    if (modalEl) {
      const modalInstance = Modal.getOrCreateInstance(modalEl);
      modalInstance.show();
    }
  };

  const openDeleteSubscriptionModal = (subscriptionId: string) => {
    ensureModalCleanup();

    setEditSubscriptionId(subscriptionId);

    const modalEl = document.getElementById("delete_subscription");
    if (modalEl) {
      const modalInstance = Modal.getOrCreateInstance(modalEl);
      modalInstance.show();
    }
  };

  const [origStartDate, setOrigStartDate] = useState<Date | null>(null);
  const [origEndDate, setOrigEndDate] = useState<Date | null>(null);
  const [origIsActive, setOrigIsActive] = useState<boolean>(false);
  const [origIsExpired, setOrigIsExpired] = useState<boolean>(false);
  const [origSubscriptionId, setOrigSubscriptionId] = useState<number | null>(
    null
  );

  const openEditModal = (sub: any) => {
    const start = sub.subscription_start_date
      ? new Date(sub.subscription_start_date)
      : null;
    const end = sub.subscription_end_date
      ? new Date(sub.subscription_end_date)
      : null;
    const active = sub.is_active === true || sub.is_active === "True";
    const expired = sub.is_expired === true || sub.is_expired === "True";
    setEditStartDate(start);
    setOrigStartDate(start);
    setEditEndDate(end);
    setOrigEndDate(end);
    setEditIsActive(active);
    setOrigIsActive(active);
    setEditIsExpired(expired);
    setOrigIsExpired(expired);
    setEditSubscriptionOptionId(sub.subscription_id);
    setOrigSubscriptionId(sub.subscription_id);
    const users = sub.number_of_user ?? 0;
    setEditNumberOfUsers(users);
    setOrigNumberOfUsers(users);
    setEditSubscriptionId(sub.tenant_subscription_id);
    Modal.getOrCreateInstance(
      document.getElementById("edit_subscription")!
    ).show();
  };

  const handleEditSave = async () => {
    if (!editSubscriptionId) return;

    const newDate = editStartDate?.toISOString().slice(0, 10) || null;
    const newEndDate = editEndDate?.toISOString().slice(0, 10) || null;
    const newActive = editIsActive;
    const newExpired = editIsExpired;
    const newSubId = editSubscriptionOptionId;

    const oldDate = origStartDate?.toISOString().slice(0, 10) || null;
    const oldEndDate = origEndDate?.toISOString().slice(0, 10) || null;
    const oldActive = origIsActive;
    const oldExpired = origIsExpired;
    const oldSubId = origSubscriptionId;

    if (
      newDate === oldDate &&
      newEndDate === oldEndDate &&
      newActive === oldActive &&
      newExpired === oldExpired &&
      newSubId === oldSubId &&
      editNumberOfUsers === origNumberOfUsers
    ) {
      return showAlert({
        icon: "info",
        title: "No changes to save",
        timer: 1200,
        showConfirmButton: false,
      });
    }

    const body: any = {
      subscription_id: newSubId,
      subscription_start_date: newDate,
      subscription_end_date: newEndDate,
      is_active: newActive,
      is_expired: newExpired,
      number_of_user: editNumberOfUsers,
    };

    setIsSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/tenant-subscription/${editSubscriptionId}/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) throw new Error();

      await showAlert({
        icon: "success",
        title: "Saved!",
        timer: 1200,
        showConfirmButton: false,
      });

      const modalEl = document.getElementById("edit_subscription")!;
      const modalInstance = Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }

      setTimeout(() => {
        document
          .querySelectorAll(".modal-backdrop")
          .forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }, 100);

      fetchSubscriptions(currentPage);
    } catch {
      await showAlert({
        icon: "error",
        title: "Save failed",
        text: "Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSubscription = async (e: MouseEvent) => {
    if (!selectedSubscriptionIds.length) {
      return showAlert({
        icon: "error",
        title: "Select at least one plan",
      });
    }

    if (!numberOfUsers || numberOfUsers <= 0) {
      return showAlert({
        icon: "error",
        title: "Number of Users is required",
        text: "Please enter a valid number of users.",
      });
    }
    setIsSaving(true);
    try {
      const authHeader = localStorage.getItem("authCred") || "";
      await Promise.all(
        selectedSubscriptionIds.map((id) =>
          fetch(`${API_BASE_URL}/tenant-subscription/`, {
            method: "POST",
            headers: {
              Authorization: `Basic ${authHeader}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              tenant_id: selectedTenantId,
              subscription_id: id,
              subscription_start_date: startDate ? startDate.toISOString().split("T")[0] : null,
              is_active: true,
              number_of_user: numberOfUsers,
            }),
          })
        )
      );

      await showAlert({
        icon: "success",
        title: "Subscriptions added!",
        timer: 1200,
        showConfirmButton: false,
      });

      setSelectedSubscriptionIds([]);
      setStartDate(new Date());

      const modalEl = document.getElementById("add_subscription")!;
      const modalInstance = Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }

      setTimeout(() => {
        document
          .querySelectorAll(".modal-backdrop")
          .forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }, 100);

      fetchSubscriptions(currentPage);
    } catch {
      await showAlert({
        icon: "error",
        title: "Add failed",
        text: "Could not add subscription.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSubscription = async (subscriptionId: string) => {
    setIsDeleting(true);
    try {
      const authHeader = localStorage.getItem("authCred") || "";
      const res = await fetch(
        `${API_BASE_URL}/tenant-subscription/${subscriptionId}/`,
        {
          method: "DELETE",
          headers: { Authorization: `Basic ${authHeader}` },
        }
      );
      if (res.ok) {
        MySwal.fire(
          "Deleted!",
          "Subscription has been deleted.",
          "success"
        ).then(() => {
          const modalEl = document.getElementById("delete_subscription");
          const modalInstance = Modal.getInstance(modalEl!);
          if (modalInstance) {
            modalInstance.hide();
          }

          setTimeout(() => {
            document
              .querySelectorAll(".modal-backdrop")
              .forEach((el) => el.remove());
            document.body.classList.remove("modal-open");
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
          }, 100);

          setEditSubscriptionId(null);
          fetchSubscriptions(currentPage);
        });
      } else {
        MySwal.fire("Error", "Failed to delete subscription.", "error");
      }
    } catch (err) {
      MySwal.fire("Error", "Failed to delete subscription.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const getPageNumbers = () => {
    const maxButtons = 3;
    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Settings</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              {/* Settings Menu */}
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <SettingsTabsNavigation
                    activeTab="subscription"
                    variant="bottom"
                    includeIcons={true}
                    context="access-subscription"
                  />
                </div>
              </div>
              {/* /Settings Menu */}
              <div className="row">
                <div className="col-xl-12 col-lg-12">
                  {/* Custom Fields */}
                  <div className="card">
                    <div className="card-body">
                      {isLoading ? (
                        <SubscriptionLoader />
                      ) : (
                        <>
                          {/* Search */}
                          <div className="row">
                            <div className="col-xl-3 col-md-3 col-lg-5">
                              <h4 className="fw-semibold mb-3">
                                Subscriptions
                              </h4>
                            </div>
                            {userRole?.role_key === "SUPERADMIN" &&
                              "PLATFORM_ANALYST" && (
                                <div className="col-xl-9 col-md-9 col-lg-12">
                                  <div className="d-flex align-items-center flex-wrap justify-content-sm-end">
                                    <button
                                      type="button"
                                      className="btn btn-primary mb-3"
                                      onClick={openAddSubscriptionModal}
                                    >
                                      <CreditCard size={16} className="me-1" />{" "}
                                      Add Subscription
                                    </button>
                                  </div>
                                </div>
                              )}
                          </div>
                          {/* /Search */}

                          {/* Contact List */}
                          <div className="table-responsive custom-table">
                            <TableContainer>
                              <table
                                className="table dataTable no-footer"
                                id="subscription-list"
                              >
                                <thead className="thead-light">
                                  <tr>
                                    <th>Plan Name</th>
                                    <th>Start Date</th>
                                    <th>Expiration Date</th>
                                    <th>Storage</th>
                                    <th>Is Active</th>
                                    <th>Is Expired</th>

                                    {userRole?.role_key === "SUPERADMIN" &&
                                      "PLATFORM_ANALYST" && <th>Action</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {subscriptions.map((sub, idx) => (
                                    <tr key={sub.tenant_subscription_id}>
                                      <td>{sub.display_name}</td>
                                      <td>
                                        {formatCustomDateTime(
                                          sub.subscription_start_date, true
                                        )}
                                      </td>
                                      <td>
                                        {formatCustomDateTime(
                                          sub.subscription_end_date, true
                                        )}
                                      </td>
                                      <td>
                                        {(() => {
                                          const totalStorage = sub.storage ? parseInt(sub.storage.total_tenant_storage) || 0 : 0;
                                          const usedStorage = sub.storage ? parseInt(sub.storage.total_tenant_used_storage) || 0 : 0;
                                          const pct = totalStorage > 0 ? (usedStorage / totalStorage) * 100 : 0;
                                          const isSuperAdmin = userRole?.role_key === "SUPERADMIN" || userRole?.role_key === "PLATFORM_ANALYST";

                                          return (
                                            <div
                                              onClick={() => !isSuperAdmin && navigate(route.storage)}
                                              style={{ cursor: isSuperAdmin ? "default" : "pointer", width: "120px" }}
                                              title={`${formatBytes(usedStorage)} of ${formatBytes(totalStorage)} used`}
                                            >
                                              <div className="progress" style={{ height: "8px", borderRadius: "4px" }}>
                                                <div
                                                  className="progress-bar bg-primary"
                                                  style={{ width: `${Math.min(pct, 100)}%` }}
                                                />
                                              </div>
                                              <small className="text-muted" style={{ fontSize: "10px" }}>
                                                {formatBytes(usedStorage)} / {formatBytes(totalStorage)}
                                              </small>
                                            </div>
                                          );
                                        })()}
                                      </td>
                                      <td>
                                        <span
                                          className={
                                            sub.is_active === "True" ||
                                              sub.is_active === true
                                              ? "badge badge-pill badge-status bg-success"
                                              : "badge badge-pill badge-status bg-danger"
                                          }
                                        >
                                          {sub.is_active === "True" ||
                                            sub.is_active === true
                                            ? "Active"
                                            : "Inactive"}
                                        </span>
                                      </td>
                                      <td>
                                        <span
                                          className={
                                            sub.is_expired === "True" ||
                                              sub.is_expired === true
                                              ? "badge badge-pill badge-status bg-danger"
                                              : "badge badge-pill badge-status bg-success"
                                          }
                                        >
                                          {sub.is_expired === "True" ||
                                            sub.is_expired === true
                                            ? "Expired"
                                            : "Not Expired"}
                                        </span>
                                      </td>
                                      {userRole?.role_key === "SUPERADMIN" &&
                                        "PLATFORM_ANALYST" && (
                                          <td>
                                            {/* Keep the action buttons as they are, or adapt as needed */}
                                            <div className="dropend table-action">
                                              <Link
                                                to="#"
                                                className="action-icon"
                                                data-bs-toggle="dropdown"
                                                data-bs-flip="false"
                                              >
                                                <i className="fa fa-ellipsis-v" />
                                              </Link>
                                              <div className="dropdown-menu">
                                                <Link
                                                  className="dropdown-item"
                                                  to="#"
                                                  onClick={() =>
                                                    openEditModal(sub)
                                                  }
                                                >
                                                  <i className="ti ti-edit text-blue" />{" "}
                                                  Edit
                                                </Link>
                                                <Link
                                                  className="dropdown-item"
                                                  to="#"
                                                  onClick={() =>
                                                    openDeleteSubscriptionModal(
                                                      sub.tenant_subscription_id
                                                    )
                                                  }
                                                >
                                                  <i className="ti ti-trash text-danger" />{" "}
                                                  Delete
                                                </Link>
                                              </div>
                                            </div>
                                          </td>
                                        )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </TableContainer>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            {(() => {
                              const startIdx = (currentPage - 1) * rowsPerPage;
                              return (
                                <small>
                                  Showing {startIdx + 1}–
                                  {Math.min(startIdx + rowsPerPage, totalCount)}{" "}
                                  of {totalCount}
                                </small>
                              );
                            })()}
                            <nav>
                              <ul className="pagination mb-0">
                                <li
                                  className={`page-item ${currentPage === 1 ? "disabled" : ""
                                    }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() =>
                                      setCurrentPage((p) => Math.max(1, p - 1))
                                    }
                                  >
                                    Prev
                                  </button>
                                </li>
                                {getPageNumbers().map((page) => (
                                  <li
                                    key={page}
                                    className={`page-item ${currentPage === page ? "active" : ""
                                      }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() => setCurrentPage(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                ))}
                                <li
                                  className={`page-item ${currentPage === totalPages ? "disabled" : ""
                                    }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() =>
                                      setCurrentPage((p) =>
                                        Math.min(totalPages, p + 1)
                                      )
                                    }
                                  >
                                    Next
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {/* /Custom Fields */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <>
        <div className="modal fade" id="add_subscription" data-bs-focus="false">
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ minWidth: "35%" }}>
            <div className="modal-content overflow-hidden border-0 shadow-lg" style={{ borderRadius: "1rem" }}>
              <div
                style={{
                  background: "#fff",
                  borderBottom: "1px solid #f1f5f9",
                  padding: "1.25rem 1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderLeft: "4px solid #4f46e5",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: "0.65rem",
                      background: "#eef2ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CreditCard size={20} color="#4f46e5" />
                  </div>
                  <div>
                    <h5 className="mb-0 fw-semibold" style={{ fontSize: 15, color: "#111827" }}>
                      Add Subscription
                    </h5>
                    <p className="mb-0 mt-1" style={{ fontSize: 12, color: "#6b7280" }}>
                      Configure and assign a new subscription plan
                    </p>
                  </div>
                </div>
                <button
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  style={{
                    background: "#f3f4f6",
                    border: "none",
                    borderRadius: "0.5rem",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#6b7280",
                    flexShrink: 0,
                  }}
                >
                  <i className="ti ti-x fs-16" />
                </button>
              </div>

              <div className="modal-body p-4" style={{ background: "#f8f9fb" }}>
                {subscriptionOptions.length > 0 && (
                  <div className="mb-3">
                    <EditSectionTitle>
                      <i className="ti ti-layout-cards" style={{ fontSize: 12 }} />
                      Available Plans
                    </EditSectionTitle>
                    <div className="row g-2">
                      {subscriptionOptions.map((opt, i) => (
                        <div className="col-12" key={opt.subscription_id}>
                          <div
                            onClick={() => selectSubscription(opt.subscription_id)}
                            style={{
                              background: TEMPLATE_ACCENTS[i % TEMPLATE_ACCENTS.length],
                              borderRadius: "0.75rem",
                              padding: "1rem 1.25rem",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              boxShadow: selectedSubscriptionIds.includes(opt.subscription_id)
                                ? "0 0 0 3px rgba(79,70,229,0.4)"
                                : "none",
                              transition: "box-shadow 0.15s ease",
                            }}
                          >
                            <div>
                              <p className="mb-0 fw-semibold text-white" style={{ fontSize: 15 }}>
                                {opt.subscription_display_name}
                              </p>
                              <p className="mb-0" style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
                                Duration: 1 Year
                              </p>
                            </div>
                            {selectedSubscriptionIds.includes(opt.subscription_id) && (
                              <div
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  background: "rgba(255,255,255,0.25)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <i className="ti ti-check text-white" style={{ fontSize: 15 }} />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="row g-3">
                  <div className="col-md-6">
                    <EditSectionCard>
                      <EditSectionTitle>
                        <i className="ti ti-users" style={{ fontSize: 12 }} />
                        License Count
                      </EditSectionTitle>
                      <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                        Number of Users
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter number of users"
                        min="1"
                        required
                        style={{ fontSize: 13 }}
                        value={numberOfUsers || ""}
                        onChange={(e) => setNumberOfUsers(parseInt(e.target.value) || 0)}
                      />
                    </EditSectionCard>
                  </div>

                  <div className="col-md-6">
                    <EditSectionCard>
                      <EditSectionTitle>
                        <i className="ti ti-calendar" style={{ fontSize: 12 }} />
                        Start Date
                      </EditSectionTitle>
                      <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                        Subscription Start Date
                      </label>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                          value={startDate}
                          onChange={setStartDate}
                          format="dd MMMM, yyyy"
                          slotProps={{
                            popper: {
                              container: () => document.getElementById("add_subscription")!,
                            },
                            textField: {
                              size: "small",
                              fullWidth: true,
                              sx: {
                                background: "#fff",
                                borderRadius: "0.375rem",
                                "& .MuiInputBase-input": { fontSize: 13, padding: "8px 12px" },
                                "& .MuiOutlinedInput-root": { borderRadius: "0.375rem" },
                              },
                            },
                          }}
                          slots={{ textField: TextField, openPickerIcon: Calendar }}
                          enableAccessibleFieldDOMStructure={false}
                        />
                      </LocalizationProvider>
                    </EditSectionCard>
                  </div>
                </div>
              </div>

              <div
                className="modal-footer"
                style={{ background: "#fff", borderTop: "1px solid #e9ecef", padding: "1rem 1.5rem" }}
              >
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  data-bs-dismiss="modal"
                  style={{ minWidth: 100 }}
                >
                  <i className="fa fa-close me-1" />
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddSubscription}
                  disabled={isSaving}
                  style={{
                    minWidth: 130,
                    background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                    border: "none",
                  }}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <i className="ti ti-plus me-1" />
                      Add Subscription
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal fade"
          id="edit_subscription"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
        >
          <div
            className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ minWidth: "50%" }}
          >
            <div className="modal-content overflow-hidden border-0 shadow-lg" style={{ borderRadius: "1rem" }}>
              <div
                style={{
                  background: "#fff",
                  borderBottom: "1px solid #f1f5f9",
                  padding: "1.25rem 1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderLeft: "4px solid #4f46e5",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: "0.65rem",
                      background: "#eef2ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CreditCard size={20} color="#4f46e5" />
                  </div>
                  <div>
                    <h5 className="mb-0 fw-semibold" style={{ fontSize: 15, color: "#111827" }}>
                      Edit Subscription
                    </h5>
                    <p className="mb-0 mt-1" style={{ fontSize: 12, color: "#6b7280" }}>
                      {subscriptions.find(
                        (s) => s.tenant_subscription_id === editSubscriptionId
                      )?.display_name || "Subscription"}
                    </p>
                  </div>
                </div>
                <button
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  style={{
                    background: "#f3f4f6",
                    border: "none",
                    borderRadius: "0.5rem",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#6b7280",
                    flexShrink: 0,
                  }}
                >
                  <i className="ti ti-x fs-16" />
                </button>
              </div>

              <div className="modal-body p-4" style={{ background: "#f8f9fb" }}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <EditSectionCard>
                      <EditSectionTitle>
                        <i className="ti ti-clipboard-list" style={{ fontSize: 12 }} />
                        Plan Details
                      </EditSectionTitle>
                      <div className="mb-3">
                        <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                          Selected Plan
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          disabled
                          style={{ background: "#fff", fontSize: 13, color: "#6b7280" }}
                          value={
                            subscriptions.find(
                              (s) => s.tenant_subscription_id === editSubscriptionId
                            )?.display_name || ""
                          }
                        />
                      </div>
                      <div>
                        <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                          Number of Users
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter number of users"
                          min="1"
                          required
                          style={{ fontSize: 13 }}
                          value={editNumberOfUsers || ""}
                          onChange={(e) =>
                            setEditNumberOfUsers(parseInt(e.target.value) || 0)
                          }
                        />
                      </div>
                    </EditSectionCard>
                  </div>

                  <div className="col-md-6">
                    <EditSectionCard>
                      <EditSectionTitle>
                        <i className="ti ti-calendar" style={{ fontSize: 12 }} />
                        Subscription Period
                      </EditSectionTitle>
                      <div className="mb-3">
                        <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                          Start Date
                        </label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DesktopDatePicker
                            value={editStartDate}
                            onChange={setEditStartDate}
                            format="dd MMMM, yyyy"
                            slotProps={{
                              popper: {
                                container: () =>
                                  document.getElementById("edit_subscription")!,
                              },
                              textField: {
                                size: "small",
                                fullWidth: true,
                                sx: {
                                  background: "#fff",
                                  borderRadius: "0.375rem",
                                  "& .MuiInputBase-input": { fontSize: 13, padding: "8px 12px" },
                                  "& .MuiOutlinedInput-root": { borderRadius: "0.375rem" },
                                },
                              },
                            }}
                            slots={{ textField: TextField, openPickerIcon: Calendar }}
                            enableAccessibleFieldDOMStructure={false}
                          />
                        </LocalizationProvider>
                      </div>
                      <div>
                        <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                          End Date
                        </label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DesktopDatePicker
                            value={editEndDate}
                            onChange={setEditEndDate}
                            format="dd MMMM, yyyy"
                            slotProps={{
                              popper: {
                                container: () =>
                                  document.getElementById("edit_subscription")!,
                              },
                              textField: {
                                size: "small",
                                fullWidth: true,
                                sx: {
                                  background: "#fff",
                                  borderRadius: "0.375rem",
                                  "& .MuiInputBase-input": { fontSize: 13, padding: "8px 12px" },
                                  "& .MuiOutlinedInput-root": { borderRadius: "0.375rem" },
                                },
                              },
                            }}
                            slots={{ textField: TextField, openPickerIcon: Calendar }}
                            enableAccessibleFieldDOMStructure={false}
                          />
                        </LocalizationProvider>
                      </div>
                    </EditSectionCard>
                  </div>

                  <div className="col-md-6">
                    <EditSectionCard>
                      <EditSectionTitle>
                        <i className="ti ti-toggle-right" style={{ fontSize: 12 }} />
                        Status
                      </EditSectionTitle>
                      <div className="d-flex gap-2">
                        <ToggleBtn
                          type="button"
                          active={!!editIsActive}
                          variant="success"
                          onClick={() => setEditIsActive(true)}
                        >
                          <i className="ti ti-circle-check" style={{ fontSize: 14 }} />
                          Active
                        </ToggleBtn>
                        <ToggleBtn
                          type="button"
                          active={!editIsActive}
                          variant="danger"
                          onClick={() => setEditIsActive(false)}
                        >
                          <i className="ti ti-circle-x" style={{ fontSize: 14 }} />
                          Inactive
                        </ToggleBtn>
                      </div>
                    </EditSectionCard>
                  </div>

                  <div className="col-md-6">
                    <EditSectionCard>
                      <EditSectionTitle>
                        <i className="ti ti-clock" style={{ fontSize: 12 }} />
                        Expiry Status
                      </EditSectionTitle>
                      <div className="d-flex gap-2">
                        <ToggleBtn
                          type="button"
                          active={!editIsExpired}
                          variant="success"
                          onClick={() => setEditIsExpired(false)}
                        >
                          <i className="ti ti-shield-check" style={{ fontSize: 14 }} />
                          Not Expired
                        </ToggleBtn>
                        <ToggleBtn
                          type="button"
                          active={!!editIsExpired}
                          variant="danger"
                          onClick={() => setEditIsExpired(true)}
                        >
                          <i className="ti ti-alert-triangle" style={{ fontSize: 14 }} />
                          Expired
                        </ToggleBtn>
                      </div>
                    </EditSectionCard>
                  </div>
                </div>
              </div>

              <div
                className="modal-footer"
                style={{ background: "#fff", borderTop: "1px solid #e9ecef", padding: "1rem 1.5rem" }}
              >
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  data-bs-dismiss="modal"
                  style={{ minWidth: 100 }}
                >
                  <i className="fa fa-close me-1" />
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleEditSave}
                  disabled={isSaving}
                  style={{
                    minWidth: 130,
                    background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                    border: "none",
                  }}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <i className="ti ti-device-floppy me-1" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Translation */}
        <div className="modal fade" id="delete_subscription" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <div className="text-center">
                  <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                    <i className="ti ti-trash-x fs-36 text-danger" />
                  </div>
                  <h4 className="mb-2">Remove Subscription?</h4>
                  <p className="mb-0">Are you sure you want to remove it?</p>
                  <div className="d-flex align-items-center justify-content-center mt-4">
                    <button
                      type="button"
                      className="btn btn-light me-2"
                      onClick={() => {
                        setEditSubscriptionId(null);
                        const modalEl = document.getElementById(
                          "delete_subscription"
                        );
                        const modalInstance = Modal.getInstance(modalEl!);
                        if (modalInstance) {
                          modalInstance.hide();
                        }
                        setTimeout(() => {
                          document
                            .querySelectorAll(".modal-backdrop")
                            .forEach((el) => el.remove());
                          document.body.classList.remove("modal-open");
                          document.body.style.overflow = "";
                          document.body.style.paddingRight = "";
                        }, 100);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() =>
                        editSubscriptionId &&
                        handleDeleteSubscription(editSubscriptionId)
                      }
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Yes, Delete it"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Delete Translation */}
      </>
    </div>
  );
};

export default SubscriptionsManagement;
